import React, { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import HomeDashboardLayout from "../HomeDashboardLayout";
import MyWalletSidebar from "../MyWalletSidebar";
import HeaderProfileSection from "../HeaderProfileSection";
import ReportModal from "../../components/ReportModal";
import StickyNotesPopup from "../../components/StickyNotesPopup";
import ViewModal from "../../components/ViewModal";
import { useDailyQuestionDetail } from "../../hooks/useDailyQuestions";
import { getStoredUser } from "../../utils/authStorage";
import { buildQuestionBankFlowState, getStoredQuestionBankFlowState, persistQuestionBankFlowState } from "../../questionBank/qbankFlowState";
import { toggleQuestionBookmark, toggleQuestionLike } from "../../services/questionBankQuestionService";
import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "../questionBank/QbankDetail.css";
import { CONTENT_ROUTES } from "../contentRoutes";
import "./DailyQuestionDetailWallet.css";

const normaliseLanguage = (value) => String(value || "EN").toUpperCase().startsWith("HI") ? "HI" : "EN";
const sanitise = (value) => DOMPurify.sanitize(String(value || ""));

const localisedValue = (item, field, language) => {
  const suffix = language === "HI" ? "hi" : "en";
  const fallback = suffix === "hi" ? "en" : "hi";
  return item?.[`${field}_${suffix}`] || item?.[field] || item?.[`${field}_${fallback}`] || "";
};

const formatSeconds = (seconds) => {
  const value = Math.max(0, Number(seconds) || 0);
  return new Date(value * 1000).toISOString().slice(11, 19);
};

const getOptionTone = (option, question) => {
  const optionId = Number(option?.option_id);
  const correctId = Number(question?.correct_option_id);
  const selectedId = Number(question?.attempted_option_id || 0);
  const wasSelected = Number(option?.user_selected_option || 0) === 1 || optionId === selectedId;

  if (option?.is_option_correct === "Y" || optionId === correctId) return "correct";
  if (wasSelected) return "wrong";
  return "neutral";
};

const DailyQuestionDetail = () => {
  const [activeModal, setActiveModal] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const flowState = useMemo(
    () => buildQuestionBankFlowState(location.state, getStoredQuestionBankFlowState()),
    [location.state]
  );
  const storedUser = getStoredUser();
  const dailyQuestionId = Number(flowState?.selectedDailyQuestion?.id || flowState?.daily_question_id || 0);
  const language = normaliseLanguage(
    flowState?.selected_lang || flowState?.language || storedUser?.content_language || storedUser?.language
  );
  const { data: question, isLoading, isError, error } = useDailyQuestionDetail({ dailyQuestionId, language });
  const queryClient = useQueryClient();
  const options = Array.isArray(question?.question_option) ? question.question_option : [];
  const detailQueryKey = ["dailyQuestionDetail", dailyQuestionId, language];
  const questionId = Number(question?.question_id || 0);
  const feedbackQuestion = question ? {
    id: questionId,
    serialNo: question.question_index,
    questionHtml: sanitise(localisedValue(question, "question_name", language)),
  } : null;
  const goBack = () => flowState?.qbankBackTarget?.pathname
    ? navigate(flowState.qbankBackTarget.pathname, { replace: true, state: flowState.qbankBackTarget.state })
    : navigate(CONTENT_ROUTES.dailyQuestions, { replace: true, state: flowState });

  useEffect(() => {
    persistQuestionBankFlowState(flowState);
  }, [flowState]);
  const updateDetailCache = (updater) => {
    queryClient.setQueryData(detailQueryKey, (current) => current ? updater(current) : current);
  };
  const bookmarkMutation = useMutation({
    mutationFn: toggleQuestionBookmark,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: detailQueryKey });
      const previous = queryClient.getQueryData(detailQueryKey);
      updateDetailCache((current) => ({ ...current, bookmark_status: Number(current.bookmark_status || 0) === 1 ? 0 : 1 }));
      return { previous };
    },
    onError: (_error, _variables, context) => context?.previous && queryClient.setQueryData(detailQueryKey, context.previous),
    onSuccess: ({ payload }) => updateDetailCache((current) => ({ ...current, bookmark_status: Number(payload?.bookmark_status ?? current.bookmark_status) })),
  });
  const likeMutation = useMutation({
    mutationFn: toggleQuestionLike,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: detailQueryKey });
      const previous = queryClient.getQueryData(detailQueryKey);
      updateDetailCache((current) => {
        const nextLiked = Number(current.like_status || 0) !== 1;
        return { ...current, like_status: nextLiked ? 1 : 0, total_liked: Math.max(0, Number(current.total_liked || 0) + (nextLiked ? 1 : -1)) };
      });
      return { previous };
    },
    onError: (_error, _variables, context) => context?.previous && queryClient.setQueryData(detailQueryKey, context.previous),
    onSuccess: ({ payload }) => updateDetailCache((current) => ({
      ...current,
      like_status: Number(payload?.like_status ?? current.like_status),
      total_liked: Number(payload?.total_liked ?? current.total_liked),
    })),
  });
  const stats = [
    { key: "bookmark", icon: question?.bookmark_status ? "bi-bookmark-fill" : "bi-bookmark", label: "Bookmark", active: Number(question?.bookmark_status) === 1, pending: bookmarkMutation.isPending },
    { key: "like", icon: question?.like_status ? "bi-heart-fill" : "bi-heart", label: "Like", value: question?.total_liked || 0, active: Number(question?.like_status) === 1, pending: likeMutation.isPending },
    { key: "view", icon: "bi-eye", label: "View", value: question?.total_views || 0, modal: "view" },
    { key: "report", icon: "bi-exclamation-circle", label: "Report", value: question?.total_reports || 0, modal: "report" },
    { key: "notes", icon: "bi-journal-text", label: "Notes", value: question?.total_notes || 0, modal: "notes" },
  ];

  const handleStatClick = (stat) => {
    if (!questionId || stat.pending) return;
    if (stat.key === "bookmark") {
      bookmarkMutation.mutate({ question_id: questionId });
      return;
    }
    if (stat.key === "like") {
      likeMutation.mutate({ question_id: questionId });
      return;
    }
    if (stat.modal) setActiveModal(stat.modal);
  };

  const renderFooterNav = (variant) => (
    <div className={`footer-nav-fixed ${variant}`}>
      <div className="qbank-stats">
        {stats.map((stat) => (
          <button key={stat.key} type="button" className={`qbank-stat${stat.active ? " is-active" : ""}`} onClick={() => handleStatClick(stat)} disabled={stat.pending} aria-pressed={stat.key === "bookmark" || stat.key === "like" ? stat.active : undefined}>
            <i className={`bi ${stat.icon}`} />
            <span>{stat.label}</span>
            {stat.value !== undefined && <strong>{stat.value}</strong>}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        .daily-question-detail-skeleton { height: 18px; border-radius: 6px; background: linear-gradient(90deg,#edf1f7 25%,#fafcff 45%,#edf1f7 65%); background-size: 240% 100%; animation: daily-detail-shimmer 1.3s infinite linear; }
        @keyframes daily-detail-shimmer { to { background-position: -240% 0; } }
      `}</style>
      <HomeDashboardLayout showHeader={false} variant="daily-question-detail-layout">
        <div className="categories-page qbank-detail-page" style={{ background: "#fff" }}>
          <div className="categories-page-header">
            <div><button type="button" className="categories-back-button" onClick={goBack}><i className="bi bi-chevron-left" /></button></div>
            <div><h1 className="categories-page-title">Daily Question Detail</h1></div>
            <div><HeaderProfileSection /></div>
          </div>

          <div className="qb-Detail-content">
            <div className="d-flex qbank-detail-main">
              <section className="qbank-LeftContent" aria-label="Question and answers">
                <div className="qbank-card qbank-card--question">
                  {isLoading ? (
                    <>
                      <div className="qbank-questionTop" aria-hidden="true">
                        <span className="daily-question-detail-skeleton" style={{ width: "20%" }} />
                        <span className="daily-question-detail-skeleton" style={{ width: "18%" }} />
                        <span className="daily-question-detail-skeleton" style={{ width: 38, height: 38, borderRadius: "50%" }} />
                      </div>
                      <div className="data-question-scroll" aria-label="Loading daily question" aria-busy="true">
                        <div className="daily-question-detail-skeleton" style={{ width: "100%", marginTop: 16 }} />
                        <div className="daily-question-detail-skeleton" style={{ width: "72%", marginTop: 12 }} />
                        {[1, 2, 3, 4].map((item) => <div className="daily-question-detail-skeleton" key={item} style={{ height: 54, marginTop: 18 }} />)}
                        <div className="daily-question-detail-skeleton" style={{ width: "32%", marginTop: 22 }} />
                      </div>
                      <div className="qbank-stats" aria-hidden="true">
                        {Array.from({ length: 5 }, (_, index) => <span className="daily-question-detail-skeleton" key={index} style={{ width: 52, height: 38 }} />)}
                      </div>
                    </>
                  ) : isError || !question ? (
                    <div className="data-question-scroll"><p className="text-danger">{error?.response?.data?.message || error?.message || "Unable to load this daily question."}</p></div>
                  ) : (
                    <>
                      <div className="qbank-questionTop">
                        <span className="qbank-progress">{question.question_index || 1} of {question.total_questions || 1}</span>
                        <div className="qbank-timer"><i className="bi bi-clock" /><span>{formatSeconds(question.question_time)}</span></div>
                        <div className="qbank-toolbar">
                          <button type="button" className="qbank-toolbarBtn" aria-label="Question information"><i className="bi bi-info-circle" /></button>
                        </div>
                      </div>
                      <div className="data-question-scroll">
                        <div className="qbank-questionBlock">
                          <h2 className="qbank-question" dangerouslySetInnerHTML={{ __html: sanitise(localisedValue(question, "question_name", language)) }} />
                        </div>
                        <div className="qbank-options" role="list" aria-label="Answer options">
                          {options.map((option, index) => (
                            <div key={option.option_id || index} className={`qbank-option qbank-option--${getOptionTone(option, question)}`} role="listitem">
                              <span className="qbank-option__badge">{String.fromCharCode(65 + index)}</span>
                              <span className="qbank-option__copy"><span className="qbank-option__english" dangerouslySetInnerHTML={{ __html: sanitise(localisedValue(option, "option_name", language)) }} /></span>
                            </div>
                          ))}
                        </div>
                        <div className="qbank-guess"><span className="qbank-guess__label">{Number(question.is_answer_correct) === 1 || question.is_answer_correct === "Y" ? "Correct answer" : "Answer review"}</span></div>
                      </div>
                      {renderFooterNav("footer-nav-fixed--desktop")}
                    </>
                  )}
                </div>
              </section>
            </div>
            {!isLoading && !isError && question && renderFooterNav("footer-nav-fixed--mobile")}
          </div>
        </div>
        <ViewModal open={activeModal === "view"} onClose={() => { setActiveModal(null); queryClient.invalidateQueries({ queryKey: detailQueryKey }); }} question={feedbackQuestion} />
        <ReportModal open={activeModal === "report"} onClose={() => { setActiveModal(null); queryClient.invalidateQueries({ queryKey: detailQueryKey }); }} question={feedbackQuestion} />
        <StickyNotesPopup open={activeModal === "notes"} onClose={() => { setActiveModal(null); queryClient.invalidateQueries({ queryKey: detailQueryKey }); }} question={feedbackQuestion} />
        <MyWalletSidebar withTopOffset={false} />
      </HomeDashboardLayout>
    </>
  );
};

export default DailyQuestionDetail;
